// Timeline-bucket model. The chart spans N_TIMELINE buckets at STEP_MIN
// granularity (192 × 15 min = 48 h). The "Now" marker default-sits at the
// midpoint (NOW_IDX = 96) — 24 h history left, 24 h forecast right.
//
// Adaptive-horizon (2026-05-28): when the available forecast data is shorter
// than 24 h (e.g. before day-ahead release at 14:00 CEST), the right side of
// the chart goes empty. To reclaim that screenspace we slide the Now-marker
// rightward via `computeNowIdx`, exposing more history on the left while
// keeping the bucket density (15 min / bucket) and total visible window
// (48 h) constant. `idxToOffsetMin`/`idxToClockTime` take an optional
// `nowIdx` so callers in adaptive mode can render relative time correctly.
export const N_TIMELINE = 192;
export const NOW_IDX = 96;
export const STEP_MIN = 15;

// Adaptive-horizon bounds. Keep at least MIN_FUTURE_BUCKETS of forecast room
// (2 h) for visual continuity at "Nu" even when there is zero forecast data
// downstream — otherwise the "Nu"-line would slam against the right edge.
// MAX_NOW_IDX caps history at 47 h, leaving the 2 h minimum on the right.
export const MIN_FUTURE_BUCKETS = 8;
export const MAX_NOW_IDX = N_TIMELINE - MIN_FUTURE_BUCKETS;

export function idxToOffsetMin(idx: number, nowIdx: number = NOW_IDX): number {
  return (idx - nowIdx) * STEP_MIN;
}

export function idxToClockTime(idx: number, now: Date, nowIdx: number = NOW_IDX): string {
  const ms = now.getTime() + idxToOffsetMin(idx, nowIdx) * 60 * 1000;
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function offsetLabel(offsetMin: number): string {
  if (offsetMin === 0) return 'Nu';
  const sign = offsetMin > 0 ? '+' : '-';
  const abs = Math.abs(offsetMin);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (m === 0) return `${sign}${h}h`;
  if (h === 0) return `${sign}${m}m`;
  return `${sign}${h}h${m}m`;
}

/**
 * Snap a date-or-timestamp into the timeline bucket relative to `now`,
 * pinned to a given `nowIdx`. Returns the integer bucket index (may be
 * outside [0, N_TIMELINE]; caller decides how to handle out-of-range).
 */
export function tsToIdxAt(ts: string | Date, now: Date, nowIdx: number = NOW_IDX): number {
  const tsMs = typeof ts === 'string' ? new Date(ts).getTime() : ts.getTime();
  const minutes = (tsMs - now.getTime()) / 60000;
  return Math.round(nowIdx + minutes / STEP_MIN);
}

/**
 * Compute the adaptive `nowIdx` based on the rightmost timestamp for which the
 * caller's chosen forecast horizon is meaningful. When that horizon is shorter
 * than the default 24h, the chart shifts "Nu" rightwards so the empty right
 * edge is replaced by more recent history. Always returns a value in
 * [NOW_IDX, MAX_NOW_IDX].
 *
 * The function itself takes the MAX of its inputs — the caller is responsible
 * for choosing *which* sources to pass. Two patterns:
 *  - "How far does ANY forecast extend?" → pass all sources (MAX-as-OR)
 *  - "Where does the LIMITING source run out?" → pass the MIN of sources
 *    pre-collapsed (MAX-as-no-op, which is what the v2 dashboard does for the
 *    strategy-driving prices + soc-forecast pair).
 *
 *   computeNowIdx(now, [pricesLastHourEnd, socFcLastHourEnd])
 *
 * If every input is null/empty, the function returns the default NOW_IDX —
 * the chart degrades gracefully into the legacy 24/24 split.
 */
export function computeNowIdx(now: Date, forecastEndTimes: (string | Date | null | undefined)[]): number {
  let maxFutureMin = 0;
  let sawValid = false;
  for (const ts of forecastEndTimes) {
    if (ts == null) continue;
    const tsMs = typeof ts === 'string' ? Date.parse(ts) : ts.getTime();
    if (!Number.isFinite(tsMs)) continue;
    sawValid = true;
    const m = (tsMs - now.getTime()) / 60000;
    if (m > maxFutureMin) maxFutureMin = m;
  }
  // "We don't know yet" state — no valid forecast end-times provided. Fall back
  // to the legacy 24/24 split (default NOW_IDX) so the chart renders identical
  // to the pre-adaptive behaviour while data is still loading.
  if (!sawValid) return NOW_IDX;

  // Convert to buckets. Cap at the default 24h forward — when forecast goes
  // beyond that, fall back to the legacy 24/24 split (we don't want to *less*
  // show history just because the forecast happens to be extra-long).
  const defaultFutureBuckets = N_TIMELINE - NOW_IDX; // = 96 (24 h)
  const futureBuckets = Math.max(
    MIN_FUTURE_BUCKETS,
    Math.min(defaultFutureBuckets, Math.ceil(maxFutureMin / STEP_MIN)),
  );
  // nowIdx = right_edge − futureBuckets. Slides right when futureBuckets shrinks.
  const nowIdx = N_TIMELINE - futureBuckets;
  // Clamp to [NOW_IDX, MAX_NOW_IDX] for safety (Math.min already covers upper).
  return Math.max(NOW_IDX, Math.min(MAX_NOW_IDX, nowIdx));
}
